import { useCallback } from 'react';

import {
  generateResourceUploadId,
  getResourceContentErrorMessage,
  isFileResourceContentType,
  RESOURCE_STATUS,
} from 'src/data/performance';
import { PROMPT_ACTION } from 'src/data/prompt';
import * as events from 'src/lib/events';

import { getPromptMenuTypeChannel, PROMPT_MENU_TYPE, promptMenuTypeToResourceType } from './lib';

export const useHandlers = ({
  availableResources,
  setMenuType,
  onCloseMenu,
  index,
  onConnectExternalChannel,
  onAction,
  uploadFileHelperRef,
  attachedResourcesRef,
}) => {
  const handleCloseMenu = useCallback(() => {
    onCloseMenu();

    // menu is closed with animation, so we need to delay this a bit
    setTimeout(() => {
      setMenuType(PROMPT_MENU_TYPE.default);
    }, 250);
  }, [onCloseMenu, setMenuType]);

  const handleUploadFile = useCallback(
    async (resourceType) => {
      // Check if user can even upload any new files
      if (!onAction(PROMPT_ACTION.validateResourceLimit, { resourceType })) {
        return;
      }

      try {
        // Request file selection from user
        const result = await uploadFileHelperRef.current.openFileDialog(resourceType);
        handleCloseMenu(); // we need to keep menu opened until user finishes with "file" dialog, as we need that "hidden" `input` to be available
        if (!result) {
          return;
        }

        // If we had a single rejection - we should display the error and return
        const rejectedFileErrorCode = result.fileRejection?.errors?.[0]?.code;
        if (rejectedFileErrorCode) {
          const errorMessage = getResourceContentErrorMessage(resourceType, rejectedFileErrorCode);
          onAction(PROMPT_ACTION.errorUploadingFile, new Error(errorMessage));
          return;
        }

        // Make sure we have a file selected
        if (!result.acceptedFile) {
          return;
        }

        // This ID is needed to track the file between several component layers
        const uploadId = generateResourceUploadId(resourceType);

        // Parent component should handle the upload
        const resource = await onAction(PROMPT_ACTION.uploadResourceFile, {
          resourceType,
          file: result.acceptedFile,
          uploadId,
          index,
        });

        // If user removed the file while we we're uploading it - we should not "insert" it
        const attachedResources = attachedResourcesRef.current;
        if (attachedResources && !attachedResources.some(({ id }) => id === uploadId)) {
          return;
        }

        if (resource?.status === RESOURCE_STATUS.active) {
          onAction(PROMPT_ACTION.insertFileResource, {
            resource,
            skipConfirmation: true,
            uploadId,
            index,
          });
        }
      } catch (error) {
        onAction(PROMPT_ACTION.errorUploadingFile, error);
      }
    },
    [onAction, uploadFileHelperRef, handleCloseMenu, index, attachedResourcesRef]
  );

  const handleSearchFocus = useCallback(() => {
    onAction(PROMPT_ACTION.trackEvent, {
      event: events.PROMPTS.promptResourceSearchDisplayed,
      trigger: 'button_click',
    });
    setMenuType(PROMPT_MENU_TYPE.all);
  }, [onAction, setMenuType]);

  const handleMenuCardClick = useCallback(
    (event) => {
      const type = event.currentTarget?.dataset?.type;
      if (!type) {
        return;
      }

      if ([PROMPT_MENU_TYPE.salesforce, PROMPT_MENU_TYPE.microsoftDynamicsSales].includes(type)) {
        onConnectExternalChannel({
          channel: getPromptMenuTypeChannel(type),
          callback: () => setMenuType(type),
        });
        return;
      }

      setMenuType(type);

      // If we don't have existing resources - execute "upload" flow right away
      const resourceType = promptMenuTypeToResourceType(type);
      if (resourceType && isFileResourceContentType(resourceType)) {
        const hasResources = availableResources?.some(
          ({ contentType }) => contentType === resourceType
        );
        if (!hasResources) {
          handleUploadFile(type);
        }
      }
    },
    [setMenuType, onConnectExternalChannel, availableResources, handleUploadFile]
  );

  return {
    handleCloseMenu,
    handleUploadFile,
    handleSearchFocus,
    handleMenuCardClick,
  };
};
